'use client';

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Link2, Copy, Check, Sparkles, Globe, 
  BarChart3, Shield, Activity, Zap, Play,
  ChevronDown, ChevronUp, Clock, Tag, Settings2, Smartphone, Lock 
} from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { toast } from 'sonner'

export default function LinkShortenerClient() {
  const [longUrl, setLongUrl] = useState('')
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [expiry, setExpiry] = useState('forever')
  const [maxClicks, setMaxClicks] = useState('')
  const [fallbackUrl, setFallbackUrl] = useState('')
  
  const [showUtm, setShowUtm] = useState(false)
  const [utm, setUtm] = useState({
    source: '',
    medium: '',
    campaign: '',
    term: '',
    content: '',
  })

  const [showAdvanced, setShowAdvanced] = useState(false)
  const [password, setPassword] = useState('')
  const [previewMode, setPreviewMode] = useState(false)
  const [qrEnabled, setQrEnabled] = useState(false)

  const [showDeepLink, setShowDeepLink] = useState(false)
  const [deepLink, setDeepLink] = useState({
    ios: '',
    android: '',
  })

  const [status, setStatus] = useState<'idle' | 'shortening' | 'done'>('idle')
  const [shortenedUrl, setShortenedUrl] = useState('')
  const [copied, setCopied] = useState(false)
  
  // Simulated analytics state
  const [clicks, setClicks] = useState(124)
  const [isSimulating, setIsSimulating] = useState(false)

  const handleShorten = (e: React.FormEvent) => {
    e.preventDefault()
    if (!longUrl) {
      toast.error('Please enter a valid destination URL')
      return
    }
    if (!title) {
      toast.error('Please enter a title')
      return
    }

    setStatus('shortening')
    setTimeout(() => {
      const generatedAlias = slug ? slug.trim().toLowerCase().replace(/[^a-z0-9-_]/g, '') : Math.random().toString(36).substring(2, 7)
      setShortenedUrl(`https://lnk.ly/r/${generatedAlias}`)
      setStatus('done')
      toast.success('Smart link generated successfully!')
    }, 1200)
  }

  const handleCopy = () => {
    if (!shortenedUrl) return
    navigator.clipboard.writeText(shortenedUrl)
    setCopied(true)
    toast.success('Shortened link copied to clipboard!')
    setTimeout(() => setCopied(false), 2000)
  }

  const runTrafficSimulation = () => {
    if (isSimulating) return
    setIsSimulating(true)
    toast.info('Starting real-time traffic simulation...')
    
    let ticks = 0
    const interval = setInterval(() => {
      setClicks(prev => prev + Math.floor(Math.random() * 4) + 1)
      ticks++
      if (ticks >= 6) {
        clearInterval(interval)
        setIsSimulating(false)
        toast.success('Simulation completed. Check your live dashboard clicks!')
      }
    }, 800)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white">
      <GeometricBackground />
      <MarketingNavbar />

      <main id="main-content">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 max-w-[1400px] mx-auto z-10 flex flex-col items-center">
          <motion.div 
            className="text-center space-y-6 max-w-3xl"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="flex items-center gap-2 justify-center">
              <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
                Enterprise URL Shortener
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.1]">
              Branded Link Shortener<br />
              <span className="text-gray-500">Custom URL tool &</span><br />
              Bitly Alternative
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-white/50 font-light max-w-xl mx-auto leading-relaxed">
              Don&apos;t just shorten your links. Elevate your brand with custom alias, custom domains, and enterprise-grade click tracking analytics.
            </motion.p>
          </motion.div>

          {/* Interactive Widget Sandbox (Dashboard Duplicate Layout) */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="w-full max-w-2xl mt-12 sharp-edge rounded-3xl border border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl p-6 md:p-8 shadow-[0_0_80px_rgba(0,102,255,0.15)] relative"
          >
            <div className="absolute top-4 right-4 flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
            </div>

            <h2 className="text-sm font-mono tracking-widest text-primary/80 uppercase mb-6 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Dashboard Link Creation Sandbox
            </h2>

            <form onSubmit={handleShorten} className="space-y-4">
              
              {/* Destination URL */}
              <div className="space-y-2">
                <label htmlFor="url-input" className="block text-[11px] font-medium text-white/50">
                  Destination URL <span className="text-primary ml-1">*</span>
                </label>
                <div className="relative">
                  <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    id="url-input"
                    type="url"
                    required
                    placeholder="https://yourlongwebsite.com/blog/how-to-scale-conversions"
                    value={longUrl}
                    onChange={(e) => setLongUrl(e.target.value)}
                    className="w-full pl-12 pr-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-sm focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20 transition-all font-light"
                  />
                </div>
              </div>

              {/* Title & Custom Slug */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label htmlFor="title-input" className="block text-[11px] font-medium text-white/50">
                    Title <span className="text-primary ml-1">*</span>
                  </label>
                  <input
                    id="title-input"
                    type="text"
                    required
                    placeholder="Spring Promo Campaign"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-sm focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20 transition-all font-medium"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="alias-input" className="block text-[11px] font-medium text-white/50 flex justify-between">
                    <span>Custom slug <span className="text-white/25 ml-1">(optional)</span></span>
                    <span className="text-[9px] text-white/30 flex items-center gap-1 font-mono">
                      Auto-generated if blank
                    </span>
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 bg-white/[0.03] border border-r-0 border-white/[0.06] rounded-l-xl text-xs text-white/25 font-mono">
                      /r/
                    </span>
                    <input
                      id="alias-input"
                      type="text"
                      placeholder="promo-sale"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      className="flex-1 px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-r-xl text-sm focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20 transition-all font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Expiry & Max Clicks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="space-y-2">
                  <label htmlFor="expiry-select" className="block text-[11px] font-medium text-white/50">Expiry Time</label>
                  <select
                    id="expiry-select"
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}
                    className="w-full px-3 py-2.5 bg-[#0e0e0e] border border-white/[0.06] rounded-xl text-xs focus:outline-none focus:border-primary/40 transition-all text-white/80"
                  >
                    <option value="forever">Forever</option>
                    <option value="1hour">1 Hour</option>
                    <option value="1day">1 Day</option>
                    <option value="7days">7 Days</option>
                    <option value="30days">30 Days</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="max-clicks-input" className="block text-[11px] font-medium text-white/50">Max Clicks (optional)</label>
                  <input
                    id="max-clicks-input"
                    type="number"
                    placeholder="Unlimited"
                    value={maxClicks}
                    onChange={(e) => setMaxClicks(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-xs focus:outline-none focus:border-primary/40 transition-all"
                  />
                </div>
              </div>

              {/* Fallback URL */}
              <div className="space-y-2 pt-1">
                <label htmlFor="fallback-input" className="block text-[11px] font-medium text-white/50">Fallback URL (optional)</label>
                <input
                  id="fallback-input"
                  type="url"
                  placeholder="e.g. https://your-site.com/expired"
                  value={fallbackUrl}
                  onChange={(e) => setFallbackUrl(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-xs focus:outline-none focus:border-primary/40 transition-all"
                />
              </div>

              {/* UTM Parameters Builder Accordion */}
              <div className="border border-white/[0.06] rounded-xl overflow-hidden mt-4">
                <button
                  type="button"
                  onClick={() => setShowUtm(!showUtm)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">UTM Parameters</span>
                    {Object.values(utm).some(v => v) && (
                      <span className="px-1.5 py-0.5 bg-primary/15 text-primary text-[10px] font-bold rounded-full">
                        {Object.values(utm).filter(v => v).length}
                      </span>
                    )}
                  </div>
                  {showUtm ? (
                    <ChevronUp className="w-4 h-4 text-white/25" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white/25" />
                  )}
                </button>
                {showUtm && (
                  <div className="p-4 space-y-3 border-t border-white/[0.06] bg-white/[0.01]">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-white/40 mb-1.5">Source *</label>
                        <input
                          type="text"
                          value={utm.source}
                          onChange={(e) => setUtm(prev => ({ ...prev, source: e.target.value }))}
                          placeholder="e.g. google, newsletter"
                          className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-white/40 mb-1.5">Medium</label>
                        <input
                          type="text"
                          value={utm.medium}
                          onChange={(e) => setUtm(prev => ({ ...prev, medium: e.target.value }))}
                          placeholder="e.g. cpc, email"
                          className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-white/40 mb-1.5">Campaign</label>
                        <input
                          type="text"
                          value={utm.campaign}
                          onChange={(e) => setUtm(prev => ({ ...prev, campaign: e.target.value }))}
                          placeholder="launch"
                          className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-white/40 mb-1.5">Term</label>
                        <input
                          type="text"
                          value={utm.term}
                          onChange={(e) => setUtm(prev => ({ ...prev, term: e.target.value }))}
                          placeholder="keywords"
                          className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-white/40 mb-1.5">Content</label>
                        <input
                          type="text"
                          value={utm.content}
                          onChange={(e) => setUtm(prev => ({ ...prev, content: e.target.value }))}
                          placeholder="banner"
                          className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Advanced Settings Accordion */}
              <div className="border border-white/[0.06] rounded-xl overflow-hidden mt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Settings2 className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Advanced Settings</span>
                    {(password || previewMode || qrEnabled) && (
                      <span className="w-2 h-2 rounded-full bg-primary" />
                    )}
                  </div>
                  {showAdvanced ? (
                    <ChevronUp className="w-4 h-4 text-white/25" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white/25" />
                  )}
                </button>
                {showAdvanced && (
                  <div className="p-4 space-y-4 border-t border-white/[0.06] bg-white/[0.01]">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-white/50 mb-1">Password Protection</label>
                        <input
                          type="password"
                          placeholder="Password (optional)"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white focus:outline-none font-mono"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[11px] font-medium text-white/50 mb-1">Preview</label>
                          <button
                            type="button"
                            onClick={() => setPreviewMode(!previewMode)}
                            className={`w-full py-2 rounded-lg text-xs font-semibold transition-all border ${
                              previewMode ? 'border-primary bg-primary/20 text-white' : 'border-white/5 text-white/40'
                            }`}
                          >
                            {previewMode ? 'Enabled' : 'Disabled'}
                          </button>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-white/50 mb-1">QR Code</label>
                          <button
                            type="button"
                            onClick={() => setQrEnabled(!qrEnabled)}
                            className={`w-full py-2 rounded-lg text-xs font-semibold transition-all border ${
                              qrEnabled ? 'border-primary bg-primary/20 text-white' : 'border-white/5 text-white/40'
                            }`}
                          >
                            {qrEnabled ? 'Enabled' : 'Disabled'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* App Deep Links Accordion */}
              <div className="border border-white/[0.06] rounded-xl overflow-hidden mt-4">
                <button
                  type="button"
                  onClick={() => setShowDeepLink(!showDeepLink)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">App Deep Links</span>
                    {(deepLink.ios || deepLink.android) && (
                      <span className="px-1.5 py-0.5 bg-primary/15 text-primary text-[10px] font-bold rounded-full">
                        {[deepLink.ios, deepLink.android].filter(Boolean).length}
                      </span>
                    )}
                  </div>
                  {showDeepLink ? (
                    <ChevronUp className="w-4 h-4 text-white/25" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white/25" />
                  )}
                </button>
                {showDeepLink && (
                  <div className="p-4 space-y-3 border-t border-white/[0.06] bg-white/[0.01]">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-white/40 mb-1">iOS Deep Link</label>
                        <input
                          type="text"
                          value={deepLink.ios}
                          onChange={(e) => setDeepLink(prev => ({ ...prev, ios: e.target.value }))}
                          placeholder="myapp://path"
                          className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-white/40 mb-1">Android Deep Link</label>
                        <input
                          type="text"
                          value={deepLink.android}
                          onChange={(e) => setDeepLink(prev => ({ ...prev, android: e.target.value }))}
                          placeholder="intent://path"
                          className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                id="shorten-btn"
                type="submit"
                disabled={status === 'shortening'}
                className="w-full py-3.5 bg-primary text-white font-bold rounded-2xl text-sm hover:bg-primary/95 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] hover:scale-[1.01] active:scale-95 disabled:opacity-50 mt-4"
              >
                {status === 'shortening' ? 'Shortening URL...' : 'Generate Short Link'}
              </button>
            </form>

            {/* Results section */}
            <AnimatePresence>
              {status === 'done' && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-6 pt-6 border-t border-white/5 space-y-6 overflow-hidden"
                >
                  <div className="p-4 bg-primary/5 border border-primary/20 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Zap className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-white/40 uppercase">Your custom link</p>
                        <a href={shortenedUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-white hover:text-primary transition-colors">
                          {shortenedUrl}
                        </a>
                      </div>
                    </div>
                    <button 
                      id="copy-short-url"
                      onClick={handleCopy}
                      className="w-full sm:w-auto px-4 py-2 bg-white text-black hover:bg-white/90 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 transition-all active:scale-95"
                    >
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copied ? 'Copied' : 'Copy URL'}</span>
                    </button>
                  </div>

                  {/* Simulated Click Tracking Area */}
                  <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="text-xs font-semibold text-white">Simulated Click Analytics</h4>
                        <p className="text-[10px] text-white/40">Watch real-time link performance simulation</p>
                      </div>
                      <button 
                        onClick={runTrafficSimulation}
                        disabled={isSimulating}
                        className="px-3 py-1.5 bg-primary/20 text-primary border border-primary/30 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-primary/30 transition-all disabled:opacity-50"
                      >
                        <Play className="w-3 h-3 fill-current" />
                        {isSimulating ? 'Tracking...' : 'Simulate Clicks'}
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 bg-white/[0.01] border border-white/5 rounded-xl">
                        <p className="text-[10px] text-white/40 uppercase">Clicks</p>
                        <p className="text-lg font-bold font-mono text-white">{clicks}</p>
                      </div>
                      <div className="p-2 bg-white/[0.01] border border-white/5 rounded-xl">
                        <p className="text-[10px] text-white/40 uppercase">Locations</p>
                        <p className="text-lg font-bold font-mono text-white">4 countries</p>
                      </div>
                      <div className="p-2 bg-white/[0.01] border border-white/5 rounded-xl">
                        <p className="text-[10px] text-white/40 uppercase">Conversion</p>
                        <p className="text-lg font-bold font-mono text-emerald-400">92.4%</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </section>

        {/* Feature Grid */}
        <section className="py-20 border-t border-white/5 relative z-10">
          <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Tag className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold uppercase tracking-tight">UTM Parameter Builder</h3>
              <p className="text-sm text-white/50 font-light leading-relaxed">
                Attach campaign parameters (source, medium, term, campaign) directly in the builder to monitor traffic sources and measure marketing attribution.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Settings2 className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold uppercase tracking-tight">Advanced Link Controls</h3>
              <p className="text-sm text-white/50 font-light leading-relaxed">
                Configure access protection, set max click limits, toggle preview templates, and configure deep routing controls for mobile apps.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold uppercase tracking-tight">Link Expiry & Passwords</h3>
              <p className="text-sm text-white/50 font-light leading-relaxed">
                Limit link lifespan or protect sensitive content with direct redirects. Safeguard digital resources from unauthorised access.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 border-t border-white/5 text-center relative z-10 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
          <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">Ready to brand your links?</h2>
          <p className="text-white/40 text-sm max-w-md mx-auto mt-4 font-light">Join creators and organizations already driving double the click-through rates with Lenklyst.</p>
          <Link 
            href="/auth/sign-up" 
            className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 bg-primary text-white font-bold rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] active:scale-95"
          >
            Get Started Free <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}
